/* eslint-disable react-hooks/rules-of-hooks */
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CopyOutlined,
  ExportOutlined,
  FormOutlined,
  PrinterOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Layout,
  Modal,
  Progress,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import axios from "axios";
import { useCookies } from "react-cookie";
import excel from "xlsx";
import { hasVacationOverlapWithAttendance } from "../../../utilites/attendanceOverlap";
import {
  buildExemptionRestFromGivenDisplay,
  buildGivenRestDisplay,
  findVacationBalanceByType,
  isExemptionVacationLabel,
} from "../../../utilites/tasksBalance";
import {
  getDefaultPayrollRange,
  getPayrollRangeForMonth,
  isValidApiDateRange,
  rangeFromPicker,
} from "../../../utilites/reportDateRange";
import { parseVacationDateValue } from "../../../utilites/vacationModalDates";
import {
  deleteVacationById,
  resolveVacationId,
} from "../../../utilites/vacationActions";
import {
  canMutateAttendanceVacation,
  isVacationSubmitDisabled,
} from "../../../utilites/settingsFlags";
import {
  getDisplayedDayLateSeconds as calcDisplayedDayLateSeconds,
  isFullApprovedVacationDay,
  resolveDayDiscountWithApprovedVacations,
  rowHasAnyAttendanceStamp,
  secondsToLateHms,
} from "../../../utilites/attendanceLateDisplay";
import {
  calcAbsenceDiscountAmount,
  calcLateDiscountAmount,
} from "../../../utilites/attendanceReportDiscounts";
import {
  computeVacationDayShiftSlice,
  fetchEmployeeShiftWindow,
  fetchVacationDurationLabel,
  getLocalShiftRangeForDate,
  shiftWindowToFormRange,
} from "../../../utilites/vacationShiftDuration";
import AppTable from "../../atoms/AppTable";
import VacationSubmitModal from "../shared/VacationSubmitModal";
import { Env, PrintFonts } from "./../../../styles";
import "./style.css";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
const { Text } = Typography;

/** استخراج اسم نوع الإجازة من شكل الاستجابة (نفس منطق صفحة سجلات الحضور) */
function pickVacationTypeLabel(vac, labelById) {
  const textFields = [
    vac?.vac_type_name,
    vac?.vactype,
    vac?.type_name,
    vac?.types,
    vac?.name,
    vac?.task_type,
    vac?.vacation_type,
    vac?.vacationtype_name,
    vac?.vacationtype?.name,
    vac?.vacationType?.name,
    vac?.label,
  ];
  for (const f of textFields) {
    if (f != null && String(f).trim() !== "") return String(f).trim();
  }
  const idCandidates = [
    vac?.vacationtype_id,
    vac?.vacationtype,
    vac?.vac_id,
    vac?.vid,
  ];
  if (Number(vac?.type) > 0) idCandidates.push(vac.type);
  for (const raw of idCandidates) {
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 0 && labelById[n]) return labelById[n];
  }
  return "";
}

/** وقت بصمة فعلي (ليس null وليس نصًا فارغًا بعد القص) */
function hasMeaningfulAttendanceStamp(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

/** وقت انصراف / خروج فعلي */
function hasMeaningfulLeaveStamp(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

/**
 * دوام يُعتبر مكتملًا: دخول + خروج في أعمدة الصف الرئيسي، وكل فترات extra_attendance كاملة إن وُجدت.
 * إن كان عدد البصمات فرديًا يُنشئ آخر فترة بلا خروج فقط، لا نُلغي الاكتمال إن كان الحضور/الانصراف الرئيسيان مكتملين.
 */
/** بصمة داخل نافذة الدوام الرسمي لليوم (من الباك اند حسب باقة/فترة durations) — خارجها فائض فقط */
function hasAttendanceInsideShift(record) {
  if (!record) return false;
  if (
    record.attendance_in_shift === undefined ||
    record.attendance_in_shift === null
  ) {
    return true;
  }
  return (
    record.attendance_in_shift == 1 ||
    record.attendance_in_shift === "1" ||
    record.attendance_in_shift === true
  );
}

function recordHasCompleteAttendance(att) {
  if (!att) return false;
  const mainOk =
    hasMeaningfulAttendanceStamp(att.attendance_time) &&
    hasMeaningfulLeaveStamp(att.leave_time);
  const extra = att.extra_attendance;
  const segOk = (seg) =>
    seg &&
    hasMeaningfulAttendanceStamp(seg.in) &&
    hasMeaningfulLeaveStamp(seg.out);
  if (!Array.isArray(extra) || extra.length === 0) {
    return mainOk;
  }
  if (extra.every(segOk)) {
    return true;
  }
  const last = extra[extra.length - 1];
  const onlyLastIncomplete =
    extra.length >= 1 &&
    !segOk(last) &&
    extra.slice(0, -1).every(segOk) &&
    mainOk;
  if (onlyLastIncomplete) {
    return true;
  }
  return false;
}

/** دوام مكتمل داخل نافذة الدوام — البصمات خارج الوقت لا تُعتبر حضورًا مكتملًا */
function recordQualifiesAsCompleteAttendance(att) {
  return recordHasCompleteAttendance(att) && hasAttendanceInsideShift(att);
}

function formatSecondsToHHMM(totalSeconds) {
  const secs = Math.max(0, Number(totalSeconds) || 0);
  const totalMinutes = Math.floor(secs / 60);
  const h = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const m = String(totalMinutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function getDayVacationsForRow(
  record,
  vacationsFallback,
  labelById,
  dailyShiftRanges,
  durationFallback,
) {
  const dateStr = record?.date;
  if (!dateStr) return [];
  let vacs = Array.isArray(record?.all_vacations) ? record.all_vacations : [];
  if (vacs.length === 0 && Array.isArray(vacationsFallback)) {
    const currentDay = dayjs(dateStr);
    vacs = vacationsFallback.filter((v) => {
      if (!v?.date_from || !v?.date_to) return false;
      const vacStart = dayjs(v.date_from).startOf("day");
      const vacEnd = dayjs(v.date_to).endOf("day");
      return (
        currentDay.isSame(vacStart, "day") ||
        currentDay.isSame(vacEnd, "day") ||
        (currentDay.isAfter(vacStart) && currentDay.isBefore(vacEnd))
      );
    });
  }

  return (vacs || [])
    .map((v) => {
      const slice = computeVacationDayShiftSlice(
        dateStr,
        v,
        dailyShiftRanges,
        durationFallback,
      );
      return {
        id: v.id ?? `${dateStr}-${Math.random()}`,
        typeLabel: pickVacationTypeLabel(v, labelById) || "",
        from: slice.from,
        to: slice.to,
        secs: slice.secs,
      };
    })
    .filter((x) => x.secs > 0 || x.typeLabel);
}
const { RangePicker } = DatePicker;

const exportToExcel = (type, fn, dl) => {
  var elt = document.getElementsByTagName("table")[0];
  if (elt) {
    var wb = excel.utils.table_to_book(elt, { sheet: "sheet1" });
    return dl
      ? excel.write(wb, { bookType: type, bookSST: true, type: "base64" })
      : excel.writeFile(wb, fn || "سجل الحضور." + (type || "xlsx"));
  }
};
export default function attendanceTable(props) {
  console.log("attendanceTable props.setting:", props.setting);
  const [cookies, setCookie, removeCookie] = useCookies(["userId"]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [refreshKey, setRefreshKey] = useState(0); // مفتاح لتحديث البيانات
  const loadRequestRef = useRef(0);
  const givenRequestRef = useRef(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isVModalVisible, setIsVModalVisible] = useState(false);
  const [isVacDetailModalVisible, setIsVacDetailModalVisible] = useState(false);
  const [currentVacDetail, setCurrentVacDetail] = useState(null);
  const [datefromValue, setDatefromValue] = useState(null);
  const [datetoValue, setDatetoValue] = useState(null);

  const [data, setData] = useState([]);
  const [load, setLoad] = useState(true);
  const [selected, setSelected] = useState([]);
  const [totalDays, setTotalDays] = useState(0);
  const [totalAtt, settotalAtt] = useState(0);
  const [totalLate, setTotalLate] = useState(0);
  const [totalLatePrice, setTotalLatePrice] = useState(0);
  const [salary, setSalary] = useState(0);
  const initialPayrollRange = getDefaultPayrollRange(props.setting);
  const [start, setStart] = useState(initialPayrollRange.start);
  const [end, setEnd] = useState(
    dayjs().isBefore(dayjs(initialPayrollRange.end, "YYYY-MM-DD"))
      ? dayjs().format("YYYY-MM-DD")
      : initialPayrollRange.end,
  );
  const [notes, setNotes] = useState("");

  const [dsalary, setDsalary] = useState(0);
  const [totalDebt, setTotalDebt] = useState(0);
  const [totalLoan, setTotalLoan] = useState(0);
  const [vacations, setVacations] = useState([]);
  const [vacationsSummary, setVacationsSummary] = useState([]);
  const [dailyDurations, setDailyDurations] = useState({});
  const [dailyShiftRanges, setDailyShiftRanges] = useState({});
  const durationFallback = useMemo(() => {
    const find = (key) => props.setting?.find((i) => i.key === key)?.value;
    return {
      start: find("duration_start") || "07:00:00",
      end: find("duration_end") || "14:00:00",
    };
  }, [props.setting]);
  const vacationSubmitDisabled = useMemo(
    () => isVacationSubmitDisabled(props.setting),
    [props.setting],
  );
  const [vacationsTypes, setVacationsTypes] = useState([]);
  const [vacationsAmount, setVacationsAmount] = useState([]);
  /** ممنوح/متبقي الفترة: نفس given-tasks وطلبات الإجازات (اعتماد الشؤون + شهر الاستهلاك) */
  const [periodTasksAmount, setPeriodTasksAmount] = useState([]);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState(null);
  const [totalVac, setTotalVac] = useState("");
  const [star, setStar] = useState(0);

  const [givenTasks, setGivenTasks] = useState(null);
  const [restTasks, setRestTasks] = useState(null);
  const [givenLoad, setGivenLoad] = useState(true);
  const [tstypes, setTstypes] = useState([]);

  const vacTypeLabelById = useMemo(() => {
    const m = {};
    if (Array.isArray(tstypes)) {
      tstypes.forEach((t) => {
        const id = Number(t.value);
        if (
          !Number.isNaN(id) &&
          t.label != null &&
          String(t.label).trim() !== ""
        ) {
          m[id] = String(t.label).trim();
        }
      });
    }
    if (Array.isArray(vacationsTypes)) {
      vacationsTypes.forEach((t) => {
        const id = Number(t.id);
        if (
          !Number.isNaN(id) &&
          t.name != null &&
          String(t.name).trim() !== ""
        ) {
          m[id] = String(t.name).trim();
        }
      });
    }
    return m;
  }, [tstypes, vacationsTypes]);

  const [totalVacs, setTotalVacs] = useState([]);
  const [pdata, setPData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs().format("MMMM"));
  const [detailedDay, setDetailedDay] = useState("");
  const [form] = Form.useForm();
  const [excludedDates, setExcludedDates] = useState([]); // الأيام المستثناة من الإعدادات
  const [dailySalary, setDailySalary] = useState(0); // الراتب اليومي لحساب خصم الغياب
  const [editingRecord, setEditingRecord] = useState(null);

  // حساب أيام الإجازات الأسبوعية من الإعدادات باستخدام useMemo لتجنب الحلقات اللانهائية
  const weekendDays = useMemo(() => {
    const weekendDaysSetting = props.setting.filter(
      (item) => item.key === "admin.weekend_days",
    )[0]?.value;

    if (weekendDaysSetting) {
      try {
        const parsedDays = JSON.parse(weekendDaysSetting);
        return Array.isArray(parsedDays) ? parsedDays : [];
      } catch (error) {
        console.log("خطأ في تحليل أيام الإجازات:", error);
        return [];
      }
    }
    return [];
  }, [props.setting]);

  const discountsRound = useMemo(() => {
    const raw = props.setting.find(
      (i) => i.key == "admin.discounts_round",
    )?.value;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [props.setting]);

  const roundDiscount = useCallback(
    (value) => {
      const n = Number(value) || 0;
      if (discountsRound > 0) {
        return Math.round(n / discountsRound) * discountsRound;
      }
      return Math.round(n);
    },
    [discountsRound],
  );

  const parseLateToMinutesValue = useCallback((late) => {
    if (!late) return 0;
    if (typeof late === "string" && late.includes(":")) {
      const p = late.split(":");
      const h = Number(p[0]) || 0;
      const m = Number(p[1]) || 0;
      return h * 60 + m;
    }
    return Number(late) || 0;
  }, []);

  const parseHmsToSecondsValue = useCallback((timeStr) => {
    if (!timeStr || timeStr === "0") return 0;
    const parts = String(timeStr).split(":");
    if (parts.length < 2) return 0;
    const h = Number(parts[0]) || 0;
    const m = Number(parts[1]) || 0;
    const s = Number(parts[2]) || 0;
    return h * 3600 + m * 60 + s;
  }, []);

  const requiredDailyMinutes = useMemo(() => {
    let totalRequiredSecs = 0;
    let requiredDaysCount = 0;

    (data || []).forEach((item) => {
      const isWeekend =
        item.is_weekend === 1 ||
        item.is_weekend === "1" ||
        item.is_weekend === true ||
        weekendDays.includes(item.dayName);
      const isHoliday = (excludedDates || []).includes(item.date);
      const isManualExcluded =
        item.is_excluded === 1 || item.is_excluded === "1";
      if (isWeekend || isHoliday || isManualExcluded) return;

      const daySecs =
        Number(dailyDurations?.[item.date]) > 0
          ? Number(dailyDurations[item.date])
          : parseHmsToSecondsValue(item.duartion);
      if (daySecs > 0) {
        totalRequiredSecs += daySecs;
        requiredDaysCount += 1;
      }
    });

    if (requiredDaysCount <= 0 || totalRequiredSecs <= 0) return 7 * 60;
    return totalRequiredSecs / 60 / requiredDaysCount;
  }, [
    data,
    weekendDays,
    excludedDates,
    dailyDurations,
    parseHmsToSecondsValue,
  ]);

  const getDailyLateDiscount = useCallback(
    (item) => {
      if (!item) return 0;
      const isWeekend =
        item.is_weekend === 1 ||
        item.is_weekend === "1" ||
        item.is_weekend === true ||
        weekendDays.includes(item.dayName);
      const isHoliday = (excludedDates || []).includes(item.date);
      const isManualExcluded =
        item.is_excluded === 1 || item.is_excluded === "1";
      const workDaySecsLate =
        Number(dailyDurations?.[item.date]) > 0
          ? Number(dailyDurations[item.date])
          : (Number(requiredDailyMinutes) || 7 * 60) * 60;
      const hasFullApprovedVacation =
        isFullApprovedVacationDay({
          totalApprovedVacSecs: Number(item.total_approved_vac_secs) || 0,
          workDaySecs: workDaySecsLate,
          finalDiscount: item.discount,
        }) ||
        item.is_full_approved_vac === 1 ||
        item.is_full_approved_vac === "1" ||
        item.is_full_approved_vac === true;
      const isWorkDay = !isWeekend && !isHoliday && !isManualExcluded;
      if (!isWorkDay || hasFullApprovedVacation) return 0;

      const safeSalary = Number(salary) || Number(props.user.salary) || 0;
      const lateSecs = (() => {
        if (!item.lateTime || item.lateTime === "0") return 0;
        const parts = String(item.lateTime).split(":");
        if (parts.length < 2) return 0;
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        const s = parseInt(parts[2], 10) || 0;
        return h * 3600 + m * 60 + s;
      })();
      if (lateSecs <= 0) return 0;
      return calcLateDiscountAmount(
        safeSalary,
        requiredDailyMinutes,
        lateSecs,
        true,
      );
    },
    [
      excludedDates,
      weekendDays,
      parseLateToMinutesValue,
      salary,
      props.user.salary,
      requiredDailyMinutes,
      dailyDurations,
    ],
  );

  /** مبلغ الخصم المعروض للصف: غياب يوم كامل = record.discount، مع بصمة = خصم التأخر فقط */
  const getDisplayedDayDiscount = useCallback(
    (record) => {
      if (!record) return 0;
      const isWeekend =
        record.is_weekend == 1 ||
        record.is_weekend === "1" ||
        record.is_weekend === true ||
        weekendDays.includes(record.dayName);
      const isHoliday = (excludedDates || []).includes(record.date);
      const isManualExcluded =
        record.is_excluded == 1 || record.is_excluded === "1";
      const isWorkDay = !isWeekend && !isHoliday && !isManualExcluded;
      const workDaySecsForDisc =
        Number(dailyDurations?.[record.date]) > 0
          ? Number(dailyDurations[record.date])
          : (Number(requiredDailyMinutes) || 7 * 60) * 60;
      const hasFullApprovedVacation =
        isFullApprovedVacationDay({
          totalApprovedVacSecs: Number(record.total_approved_vac_secs) || 0,
          workDaySecs: workDaySecsForDisc,
          finalDiscount: record.discount,
        }) ||
        record.is_full_approved_vac === 1 ||
        record.is_full_approved_vac === "1" ||
        record.is_full_approved_vac === true;
      if (!isWorkDay || hasFullApprovedVacation) return 0;
      if (!recordQualifiesAsCompleteAttendance(record)) {
        return Number(record.discount) || 0;
      }
      return getDailyLateDiscount(record);
    },
    [
      excludedDates,
      weekendDays,
      getDailyLateDiscount,
      dailyDurations,
      requiredDailyMinutes,
    ],
  );

  const getDisplayedDayLateSeconds = useCallback(
    (record) =>
      calcDisplayedDayLateSeconds(record, {
        recordQualifiesAsCompleteAttendance,
        getDisplayedDayDiscount,
        dailyDurations,
        requiredDailyMinutes,
        salary: Number(salary) || Number(props.user.salary) || 0,
      }),
    [
      getDisplayedDayDiscount,
      dailyDurations,
      requiredDailyMinutes,
      salary,
      props.user.salary,
    ],
  );

  const localTotals = useMemo(() => {
    let lateMinutes = 0;
    let lateSeconds = 0; // For toHHMMSS alignment
    let attDays = 0;
    let absentDays = 0;
    let workSecs = 0;
    let vacSecs = 0;
    let bonusSecs = 0;
    let lateDeductions = 0;
    let absentDeductions = 0;

    const parseTimeToSeconds = (timeStr) => {
      if (!timeStr || timeStr === "0") return 0;
      const parts = timeStr.split(":");
      if (parts.length < 2) return 0;
      const h = parseInt(parts[0]) || 0;
      const m = parseInt(parts[1]) || 0;
      const s = parseInt(parts[2]) || 0;
      return h * 3600 + m * 60 + s;
    };

    (data || []).forEach((item) => {
      const hasAnyAttendanceStamp =
        hasMeaningfulAttendanceStamp(item.attendance_time) ||
        hasMeaningfulLeaveStamp(item.leave_time) ||
        (Array.isArray(item.extra_attendance) &&
          item.extra_attendance.some(
            (seg) =>
              hasMeaningfulAttendanceStamp(seg?.in) ||
              hasMeaningfulLeaveStamp(seg?.out),
          ));
      const vacationTypeId = Number(item.type ?? item.vacationtype_id ?? 0);
      const vacationTypeName = String(item.vac_type_name || item.types || "");
      const isHrApprovedVacation =
        Number(item.accepted) === 2 || Number(item.vac_status) === 2;
      const isMissionVacation =
        isHrApprovedVacation &&
        (vacationTypeId === 1 || vacationTypeName.includes("مهمة"));
      const attendedForActualCount =
        (hasAnyAttendanceStamp && hasAttendanceInsideShift(item)) ||
        isMissionVacation;
      if (attendedForActualCount) attDays++;

      // يوم غير مطلوب دوام (باقة/فترة) — يوافق is_weekend من attendancelog / excluded-dates
      const isWeekend =
        item.is_weekend == 1 ||
        item.is_weekend === "1" ||
        item.is_weekend === true;
      const isHoliday = (excludedDates || []).includes(item.date);
      const isManualExcluded = item.is_excluded == 1 || item.is_excluded == "1";
      const isWorkDay = !isWeekend && !isHoliday && !isManualExcluded;

      // Count absence only if approved vacation fully covers the whole day.
      // For partial vacations we must still apply the remaining discount.
      const workDaySecs =
        Number(dailyDurations?.[item.date]) > 0
          ? Number(dailyDurations[item.date])
          : (Number(requiredDailyMinutes) || 7 * 60) * 60;
      const approvedVacSecs = Number(item.total_approved_vac_secs) || 0;
      const hasFullApprovedVacation = isFullApprovedVacationDay({
        totalApprovedVacSecs: approvedVacSecs,
        workDaySecs,
        finalDiscount: item.discount,
      }) ||
        item.is_full_approved_vac === 1 ||
        item.is_full_approved_vac === "1" ||
        item.is_full_approved_vac === true;

      // غياب = يوم عمل بلا بصمة ولا إجازة معتمدة تغطي اليوم بالكامل
      if (isWorkDay && !hasAnyAttendanceStamp && !hasFullApprovedVacation) {
        absentDays++;
      }

      if (isWorkDay && !hasFullApprovedVacation && !isManualExcluded) {
        if (hasAnyAttendanceStamp) {
          const dayLateSecs = getDisplayedDayLateSeconds(item);
          if (dayLateSecs > 0) {
            lateSeconds += dayLateSecs;
          }
        }
      }

      workSecs += parseTimeToSeconds(item.workHours);
      vacSecs += parseTimeToSeconds(item.vacHours);
      bonusSecs += parseTimeToSeconds(item.bonusTime);
    });

    const safeSalary = Number(salary) || Number(props.user.salary) || 0;
    absentDeductions = calcAbsenceDiscountAmount(
      safeSalary,
      dsalary,
      absentDays,
    );
    lateDeductions = calcLateDiscountAmount(
      safeSalary,
      requiredDailyMinutes,
      lateSeconds,
      true,
    );
    lateMinutes = Math.round(lateSeconds / 60);

    const itemsCount = data?.length || 30;
    const totalPotentialSalary =
      props.user.status == 16 ? safeSalary : safeSalary * itemsCount;
    let totalDeductions = Number(lateDeductions) + Number(absentDeductions);

    // Apply administrative rounding if set
    const dround =
      props.setting.find((i) => i.key == "admin.discounts_round")?.value || 0;
    if (dround > 0) {
      totalDeductions = Math.round(totalDeductions / dround) * dround;
    } else {
      totalDeductions = Math.round(totalDeductions);
    }

    const disciplinePercentage =
      totalPotentialSalary > 0
        ? Math.max(0, Math.min(1, 1 - totalDeductions / totalPotentialSalary))
        : 0;

    return {
      lateMinutes,
      lateSeconds,
      attDays,
      absentDays,
      lateDeductions,
      absentDeductions,
      discounts: totalDeductions,
      workSecs,
      vacSecs,
      bonusSecs,
      disciplinePercentage,
      totalDeductions,
    };
  }, [
    data,
    weekendDays,
    excludedDates,
    parseLateToMinutesValue,
    getDailyLateDiscount,
    getDisplayedDayDiscount,
    getDisplayedDayLateSeconds,
    salary,
    totalDays,
    dsalary,
    props.user,
    pdata.length,
    requiredDailyMinutes,
    dailyDurations,
  ]);

  let curr = props.setting.filter((item) => item.key == "admin.currency")[0]
    ?.value;

  // دالة لتوليد جميع الأيام في النطاق الزمني
  const generateAllDays = (startDate, endDate) => {
    const days = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let current = start;

    // أسماء الأيام بالعربية (0 = الأحد، 6 = السبت)
    const dayNames = [
      "الأحد",
      "الإثنين",
      "الثلاثاء",
      "الأربعاء",
      "الخميس",
      "الجمعة",
      "السبت",
    ];

    while (current.isBefore(end) || current.isSame(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");
      const dayOfWeek = current.day(); // 0 = الأحد، 6 = السبت
      const arDayName = dayNames[dayOfWeek];

      days.push({
        date: dateStr,
        dayName: arDayName,
        attendance_time: null,
        leave_time: null,
        workHours: "00:00:00",
        vacHours: "00:00:00",
        lateTime: "00:00:00",
        discount: 0,
        types: null,
        bonusTime: "00:00:00",
        notes: "",
        have_vac: "0",
        extra_attendance: [],
      });

      current = current.add(1, "day");
    }

    return days;
  };

  // دالة لتنسيق الوقت
  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    return dayjs(dateTimeString, "YYYY-MM-DD HH:mm:ss")
      .locale("en") // يضمن عرض AM/PM
      .format("HH:mm:ss");
  };

  // دالة لدمج بيانات الحضور مع جميع الأيام
  const mergeAttendanceData = useCallback(
    (
      attendanceData,
      allDays,
      excludedDatesList = [],
      dailySalaryValue = 0,
      weekendDaysList = [],
      baseSalary = 0,
      vacationsList = [],
      dailyDurationsMap = {},
      dailyShiftRangesMap = {},
      durationFallback = null,
    ) => {
      // خصم يوم كامل / بصمة ناقصة: يطابق صيغة التأخرات = الراتب / (دقائق_اليوم × 30) × دقائق_اليوم = الراتب / 30
      const fullDayGross =
        baseSalary > 0
          ? baseSalary / 30
          : dailySalaryValue > 0
            ? dailySalaryValue
            : 0;

      // إنشاء خريطة لبيانات الحضور باستخدام التاريخ كمفتاح
      const attendanceMap = new Map();
      attendanceData.forEach((item) => {
        const existing = attendanceMap.get(item.date);
        // نفضل السجل المعتمد إذا كان لدينا دبلومات لنفس التاريخ (بسبب الـ join في الباك إند)
        if (!existing || Number(item.accepted) > Number(existing.accepted)) {
          attendanceMap.set(item.date, item);
        }
      });

      // دمج البيانات
      const mergedData = allDays.map((day) => {
        const attendance = attendanceMap.get(day.date);
        const currentDay = dayjs(day.date);

        // البحث عن جميع الإجازات لهذا اليوم من القائمة الكلية
        const matchingVacations = Array.isArray(vacationsList)
          ? vacationsList.filter((v) => {
              if (!v.date_from || !v.date_to) return false;
              const vacStart = dayjs(v.date_from).startOf("day");
              const vacEnd = dayjs(v.date_to).endOf("day");
              // التحقق من تداخل اليوم المختار مع فترة الإجازة بشكل أكثر دقة
              return (
                currentDay.isSame(vacStart, "day") ||
                currentDay.isSame(vacEnd, "day") ||
                (currentDay.isAfter(vacStart) && currentDay.isBefore(vacEnd))
              );
            })
          : [];

        // التحقق من حالة الاعتماد من الشؤون (قبول = 2) لخصم المبلغ
        // مع ملاحظة أن الحالة 1 (مدير الإدارة) لا ترفع الخصم حسب رغبة المستخدم
        const approvedVacations = matchingVacations.filter(
          (v) => Number(v.accepted) === 2,
        );
        const firstVacationNote =
          matchingVacations[0]?.description ||
          matchingVacations[0]?.notes ||
          "";
        const isHRApproved =
          (attendance && Number(attendance.accepted) === 2) ||
          approvedVacations.length > 0;

        // حساب إجمالي مدة الإجازات المعتمدة من الشؤون لهذا اليوم (بالثواني)
        let totalApprovedVacSecs = 0;
        approvedVacations.forEach((v) => {
          const slice = computeVacationDayShiftSlice(
            day.date,
            v,
            dailyShiftRangesMap,
            durationFallback,
          );
          totalApprovedVacSecs += slice.secs || 0;
        });

        // حساب "رصيد الإجازة المعتمدة" بالعملة (خصم جزئي)
        // استخدام مدة الدوام الفعلية لهذا اليوم من الباك إند، مع الاحتياط بـ 7 ساعات
        const workDaySecs = dailyDurationsMap[day.date] || 7 * 3600;
        const vacationCredit =
          workDaySecs > 0 && fullDayGross > 0
            ? (totalApprovedVacSecs / workDaySecs) * fullDayGross
            : 0;

        if (attendance) {
          // يوم ليس من أيام الدوام المطلوبة في باقة/فترة durations (نفس buildDailyRows في الباك)
          const isOffDayBySchedule =
            attendance.is_weekend == 1 ||
            attendance.is_weekend === "1" ||
            attendance.is_weekend === true;

          // التحقق من إذا كان اليوم مستثنى (عطلة، يوم عطلة أسبوعية عام/خاص، أو يوم ليس بيوم دوام)
          const isExcluded =
            attendance.is_excluded === 1 ||
            attendance.is_excluded === "1" ||
            excludedDatesList.includes(day.date) ||
            isOffDayBySchedule;

          let finalDiscount = Number(attendance.discount) || 0;
          const hasCompleteAttendance =
            recordQualifiesAsCompleteAttendance(attendance);

          // إذا كان اليوم مستثنى، يجب أن يكون discount = 0 دائماً
          if (isExcluded) {
            finalDiscount = 0;
          } else if (!hasCompleteAttendance) {
            finalDiscount = resolveDayDiscountWithApprovedVacations({
              fullDayGross,
              backendDiscount: Number(attendance.discount) || 0,
              totalApprovedVacSecs,
              vacationCredit,
              hasAnyAttendanceStamp: rowHasAnyAttendanceStamp(attendance),
            });
          }

          const isFullApprovedVac = isFullApprovedVacationDay({
            totalApprovedVacSecs,
            workDaySecs,
            finalDiscount,
          });

          return {
            ...day,
            ...attendance,
            lateTime: attendance?.lateTime || "00:00:00",
            discount: Number(finalDiscount) || 0,
            total_approved_vac_secs: totalApprovedVacSecs,
            is_full_approved_vac: isFullApprovedVac ? 1 : 0,
            is_excluded: isExcluded ? 1 : 0,
            vac_id:
              attendance?.vac_id ||
              approvedVacations[0]?.id ||
              matchingVacations[0]?.id,
            accepted:
              attendance?.accepted !== undefined
                ? attendance.accepted
                : approvedVacations[0]?.accepted ||
                  matchingVacations[0]?.accepted,
            vac_type_name:
              attendance?.vac_type_name ||
              approvedVacations[0]?.vac_type_name ||
              matchingVacations[0]?.vac_type_name,
            all_vacations: matchingVacations,
            notes: attendance?.notes || firstVacationNote,
            // +
            // (!hasCompleteAttendance && !isExcluded && !isHRApproved
            //   ? "غياب أو بصمة غير مكتملة (خصم يوم كامل)"
            //   : "")
            is_weekend: isOffDayBySchedule
              ? 1
              : attendance.is_weekend != null
                ? attendance.is_weekend
                : 0,
          };
        }

        // إذا لم تكن هناك بيانات حضور من API (يوم غياب كامل لم يظهر في السجلات)
        const backendRow = Array.isArray(attendanceData)
          ? attendanceData.find((d) => d && d.date === day.date)
          : null;
        const isOffByScheduleFromBackend = backendRow
          ? backendRow.is_weekend == 1 ||
            backendRow.is_weekend === "1" ||
            backendRow.is_weekend === true
          : false;
        const isExcluded =
          excludedDatesList.includes(day.date) || isOffByScheduleFromBackend;

        let discount = 0;
        if (isExcluded) {
          discount = 0;
        } else if (totalApprovedVacSecs > 0) {
          discount = Math.max(0, fullDayGross - vacationCredit);
        } else {
          const backendDay = attendanceData.find((d) => d.date === day.date);
          discount =
            backendDay && backendDay.discount !== undefined
              ? Number(backendDay.discount)
              : fullDayGross;
        }

        const isFullApprovedVac = isFullApprovedVacationDay({
          totalApprovedVacSecs,
          workDaySecs,
          finalDiscount: discount,
        });

        return {
          ...day,
          discount: discount,
          total_approved_vac_secs: totalApprovedVacSecs,
          is_full_approved_vac: isFullApprovedVac ? 1 : 0,
          is_excluded: isExcluded ? 1 : 0,
          is_weekend: isOffByScheduleFromBackend
            ? 1
            : day.is_weekend != null
              ? day.is_weekend
              : 0,
          vac_id: approvedVacations[0]?.id || matchingVacations[0]?.id,
          accepted:
            approvedVacations[0]?.accepted || matchingVacations[0]?.accepted,
          vac_status:
            approvedVacations[0]?.accepted || matchingVacations[0]?.accepted, // التأكد من تمرير الحالة للأيقونة حتى عند الغياب
          have_vac: matchingVacations.length > 0 ? "1" : "0", // ضمان ظهور الأيقونة عند وجود إجازة في يوم غياب
          vac_type_name:
            approvedVacations[0]?.vac_type_name ||
            matchingVacations[0]?.vac_type_name,
          all_vacations: matchingVacations,
          notes:
            !isExcluded && !isHRApproved ? "غياب يوم كامل" : firstVacationNote,
        };
      });

      // ترتيب البيانات حسب التاريخ
      return mergedData.sort((a, b) => {
        return dayjs(b.date).unix() - dayjs(a.date).unix();
      });
    },
    [],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // حماية من التواريخ غير الصالحة قبل أي طلب
    if (!isValidApiDateRange(start, end)) {
      return;
    }

    const requestId = ++loadRequestRef.current;
    setLoad(true);
    setData([]);
    originalAttendanceDataRef.current = [];

    // جلب الأيام المستثناة من الإعدادات (type=1 في جدول dates + أيام الإجازات الأسبوعية)
    // نحاول استخدام API موجود أو نستخدم منطق بديل
    axios
      .get(
        Env.HOST_SERVER_NAME +
          "excluded-dates/" +
          props.user.user_id +
          "/" +
          start +
          "/" +
          end,
      )
      .then((response) => {
        if (requestId !== loadRequestRef.current) return;
        // إذا كان API موجود ويعيد قائمة بالتواريخ المستثناة
        let excluded = [];
        if (Array.isArray(response.data)) {
          // إذا كانت البيانات مصفوفة من strings (تواريخ مباشرة)
          excluded = response.data.map((item) => {
            if (typeof item === "string") {
              return item;
            }
            // إذا كانت كائنات تحتوي على date
            return item.date || item;
          });
        }
        setExcludedDates(excluded);
      })
      .catch(function (error) {
        if (requestId !== loadRequestRef.current) return;
        // إذا لم يكن API موجود، نحسب الأيام المستثناة من الفرق
        // بين إجمالي الأيام والأيام المطلوبة (type IS NULL)
        console.log("API excluded-dates not found, using alternative method");
        setExcludedDates([]);
      });

    axios
      .get(
        Env.HOST_SERVER_NAME +
          "attendancelog/" +
          props.user.user_id +
          "/" +
          start +
          "/" +
          end,
      )
      .then((response) => {
        if (requestId !== loadRequestRef.current) return;
        // حفظ البيانات الأصلية من API
        originalAttendanceDataRef.current = response.data || [];

        // توليد جميع الأيام في النطاق الزمني
        const allDays = generateAllDays(start, end);
        // دمج بيانات الحضور مع جميع الأيام (مع حساب الخصم للأيام بدون حضور)
        // نستخدم excludedDates الحالية (قد تكون فارغة في البداية، لكن سيتم تحديثها لاحقاً)
        const mergedData = mergeAttendanceData(
          originalAttendanceDataRef.current,
          allDays,
          excludedDates,
          dailySalary,
          weekendDays,
          salary || props.user.salary || 0,
          vacations,
          dailyDurations,
          dailyShiftRanges,
          durationFallback,
        );
        setData(mergedData);
      })
      .catch(function (error) {
        if (requestId !== loadRequestRef.current) return;
        console.log(error);
        // في حالة الخطأ، نعرض على الأقل جميع الأيام
        const allDays = generateAllDays(start, end);
        setData(allDays);
        setPData(allDays);
      });

    axios
      .get(
        Env.HOST_SERVER_NAME +
          "dawam-info/" +
          props.user.user_id +
          "/" +
          start +
          "/" +
          end,
      )
      .then((response) => {
        if (requestId !== loadRequestRef.current) return;
        setTotalDays(response.data.count?.[0]?.count || 0);
        settotalAtt(response.data.data?.[0]?.attendanceDays || 0);
        setTotalLate(response.data.data?.[0]?.lateTime || 0);
        const avgRequiredDailyMinutes = (() => {
          const vals = Object.values(response.data.daily_durations || {})
            .map((v) => Number(v) || 0)
            .filter((v) => v > 0);
          if (!vals.length) return 7 * 60;
          return vals.reduce((a, b) => a + b, 0) / vals.length / 60;
        })();
        const computedLateTimePrice =
          avgRequiredDailyMinutes > 0
            ? ((Number(response.data.data?.[0]?.salary || 0) || 0) /
                (avgRequiredDailyMinutes * 30)) *
              (Number(response.data.data?.[0]?.lateTime || 0) || 0)
            : Number(response.data.data?.[0]?.lateTimePrice || 0);
        setTotalLatePrice(computedLateTimePrice);
        const salaryValue = response.data.data?.[0]?.salary || 0;
        const totalDaysValue = response.data.count?.[0]?.count || 0;
        setSalary(salaryValue);
        const dsalaryFromAPI = response.data.data?.[0]?.dsalary || 0;
        setDsalary(dsalaryFromAPI);
        // استخدام الراتب اليومي من API (getDialySalary) بدلاً من الحساب المحلي
        // هذا يضمن أن الخصم يكون لليوم نفسه وليس لكل الأيام
        setDailySalary(dsalaryFromAPI);
        setVacations(response.data.vacs_list || []);
        setVacationsSummary(response.data.vacs || []);
        setDailyDurations(response.data.daily_durations || {});
        setDailyShiftRanges(response.data.daily_shift_ranges || {});
        setVacationsTypes(response.data.vacstypes);
        setVacationsAmount(response.data.tasksAmount);
        setTotalVacs(response.data.totalvacs);
        setTotalDebt(response.data.debt?.[0]?.["amount"] || 0);
        setTotalLoan(response.data.long_debt?.[0]?.["amount"] || 0);
        console.log(computedLateTimePrice);

        // حساب نسبة الانضباط بشكل آمن
        // التعامل مع lists ككائن أو array
        const listsData = Array.isArray(response.data.lists)
          ? response.data.lists[0]
          : response.data.lists;

        const lateTimePrice = parseFloat(computedLateTimePrice || 0);
        const totalCount = parseInt(response.data.count?.[0]?.count || 0);
        const attendanceDays = parseInt(listsData?.["attendanceDays"] || 0);
        const salary = parseFloat(listsData?.salary || 0);

        let starValue = 0;

        // التأكد من أن الراتب ليس صفراً قبل الحساب
        if (salary > 0 && totalCount > 0) {
          const absentDays = totalCount - attendanceDays;
          // Use exact daily salary from API to match wages report logic
          // Fallback to salary / totalCount if API value is missing
          const dsalaryRate =
            dsalaryFromAPI > 0 ? dsalaryFromAPI : salary / totalCount;
          const absentDaysCost = absentDays * dsalaryRate;
          const totalDeductions = lateTimePrice + absentDaysCost;
          starValue = 1 - totalDeductions / salary;

          // التأكد من أن النتيجة بين 0 و 1
          starValue = Math.max(0, Math.min(1, starValue));
        } else if (salary > 0) {
          // إذا كان الراتب موجود ولكن لا توجد أيام عمل، النسبة 0
          starValue = 0;
        }

        // التأكد من أن القيمة ليست NaN
        if (isNaN(starValue) || !isFinite(starValue)) {
          starValue = 0;
        }

        setStar(starValue);
        setLoad(false);
      })
      .catch(function (error) {
        if (requestId !== loadRequestRef.current) return;
        console.log("API Error:", error);
        // إضافة قيم افتراضية في حالة الخطأ
        setTotalDebt(0);
        setTotalLoan(0);
        setStar(0);
        setLoad(false);
      });

    axios
      .get(
        `${Env.HOST_SERVER_NAME}given-tasks/${props.user.user_id}/${start}/${end}`,
      )
      .then((res) => {
        if (requestId !== loadRequestRef.current) return;
        setPeriodTasksAmount(res.data?.tasksAmount || []);
      })
      .catch(() => {
        if (requestId !== loadRequestRef.current) return;
        setPeriodTasksAmount([]);
      });

    axios
      .get(Env.HOST_SERVER_NAME + "get-tasks-types")
      .then((response) => {
        if (requestId !== loadRequestRef.current) return;
        setTstypes(
          response.data.map((t) => ({ ...t, value: Number(t.value) })),
        );
        //setLoadt(false);
      })
      .catch(function (error) {
        if (requestId !== loadRequestRef.current) return;
        console.log(error);
      });
  }, [start, end, props.user.user_id, mergeAttendanceData, refreshKey]);

  // useEffect منفصل لتحديث البيانات عند تحديث excludedDates أو dailySalary
  // نستخدم useRef لتخزين البيانات الأصلية من API
  const originalAttendanceDataRef = useRef([]);

  useEffect(() => {
    if (originalAttendanceDataRef.current.length > 0) {
      // إعادة دمج البيانات مع excludedDates المحدثة
      const allDays = generateAllDays(start, end);
      const mergedData = mergeAttendanceData(
        originalAttendanceDataRef.current,
        allDays,
        excludedDates,
        dailySalary,
        weekendDays,
        salary,
        vacations,
        dailyDurations,
        dailyShiftRanges,
        durationFallback,
      );
      setData(mergedData);
    }
  }, [
    excludedDates,
    dailySalary,
    start,
    end,
    weekendDays,
    salary,
    vacations,
    dailyDurations,
    dailyShiftRanges,
    durationFallback,
    mergeAttendanceData,
  ]);

  // جميع الأيام تظهر (حتى المستثناة) لتقديم المهام والإجازات
  // لكن الخصم = 0 للأيام المستثناة

  useEffect(() => {
    setPData(data);
  }, [data]);

  // useEffect لتعيين start و end بعد التأكد من تحميل الإعدادات
  useEffect(() => {
    if (
      !props.setting ||
      !props.setting.length ||
      !props.setting.find((item) => item.key == "admin.month_start")
    ) {
      return;
    }
    const range = getDefaultPayrollRange(props.setting);
    setStart(range.start);
    const monthEnd = dayjs(range.end, "YYYY-MM-DD");
    setEnd(
      dayjs().isBefore(monthEnd) ? dayjs().format("YYYY-MM-DD") : range.end,
    );
  }, [props.setting]);

  const disabledDate = (current) => {
    // Calculate the date two days ago
    var daysCount =
      props.setting.filter((item) => item.key == "admin.vacations_tolerance")[0]
        ?.value * 1;
    var count = isNaN(daysCount) ? 1 : daysCount;

    const twoDaysAgo = dayjs().subtract(count, "day").endOf("day");

    // Disable dates that are before two days ago
    return current && current < twoDaysAgo;
  };
  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    if (sorter.order) {
      data.sort((a, b) => {
        return sorter.column.sorter(a, b);
      });
      setPData(sorter.order == "descend" ? data.reverse() : data);
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key] != null) {
          setPData(data.filter((item) => filters[key].includes(item[key])));
        }
      });
    }
  };
  const changeRange = (dates, dateStrings) => {
    const range = rangeFromPicker(dates, dateStrings);
    if (!range) return;
    setStart(range.start);
    setEnd(range.end);
    setRefreshKey((prev) => prev + 1);
  };
  const printReport = () => {
    var report = document.getElementById("att-report");

    //var report=document.body;
    var mywindow = window.open("");
    mywindow.document.write(
      "<html><head><title></title> <style>" +
        PrintFonts.getPrintFontsCSS() +
        "body{font-size:12px;margin:0} " +
        "</style><style type='text/css' media='print'>@page { size: A4 landscape; print-color-adjust: exact !important;  -webkit-print-color-adjust: exact !important;}</style>",
    );
    mywindow.document.write('</head><body dir="rtl" style="font-size:12px;" >');
    mywindow.document.write(report.innerHTML);
    mywindow.document.write("</body></html>");
    mywindow.print(); // change window to mywindow
    // mywindow.close();

    /*        var printContents = document.getElementById("att-report").innerHTML;
        var originalContents = document.body.innerHTML;
    
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;*/
  };
  const showModal = (record) => {
    setDetailedDay(record.date);
    axios
      .get(
        Env.HOST_SERVER_NAME +
          "attendancelogs/" +
          props.user.user_id +
          "/" +
          record.date,
      )
      .then((response) => {
        setSelected(response.data);
      })
      .catch(function (error) {
        console.log(error);
      });

    setIsModalVisible(true);
  };
  const showVacationModal = (record, isEdit = false) => {
    console.log(
      "showVacationModal called with record:",
      record,
      "isEdit:",
      isEdit,
    );

    if (!isEdit && vacationSubmitDisabled) {
      notification.warning({
        message: "تقديم الإجازات موقوف",
        description:
          "تم إيقاف تقديم الإجازات من الإعدادات العامة. تواصل مع الإدارة.",
        placement: "bottomRight",
      });
      return;
    }

    if (isEdit) {
      if (!canMutateAttendanceVacation(record, props.setting)) {
        notification.warning({
          message: "تنبيه",
          description:
            "لا يمكن تعديل هذه الإجازة في حالتها الحالية. فعّل «تعديل وحذف الإجازات بعد الاعتماد» من الإعدادات العامة إن كان مسموحاً.",
          placement: "bottomRight",
        });
        return;
      }
      // إعداد البيانات للتعديل
      setEditingRecord({
        ...record,
        vac_id: record.id || record.vac_id, // استخدام المعرف الصحيح
      });
    } else {
      // إرسال طلب جديد - نمرر فقط التاريخ والمستخدم
      setNotes("");
      setType(null);
      setTotalVac("");
      const vacDate = record.date || dayjs().format("YYYY-MM-DD");
      const shiftFromMap = dailyShiftRanges?.[vacDate];
      setEditingRecord({
        date: vacDate,
        shift_start: record.shift_start || shiftFromMap?.start || null,
        shift_end: record.shift_end || shiftFromMap?.end || null,
      });
    }

    setIsVModalVisible(true);
  };

  const removeVacationFromLocalState = (vacationId) => {
    const vid = Number(vacationId);
    if (!Number.isFinite(vid) || vid <= 0) return;

    setVacations((prev) =>
      (prev || []).filter((v) => Number(v.id) !== vid),
    );
    setData((prev) =>
      (prev || []).map((row) => {
        const nextAll = (row.all_vacations || []).filter(
          (v) => Number(v.id) !== vid,
        );
        const stillHasVac = nextAll.length > 0;
        return {
          ...row,
          all_vacations: nextAll,
          have_vac: stillHasVac ? "1" : "0",
          vac_id: stillHasVac ? nextAll[0]?.id : null,
          vac_status: stillHasVac ? nextAll[0]?.accepted : null,
        };
      }),
    );
    setCurrentVacDetail((prev) => {
      if (!prev) return prev;
      const nextAll = (prev.all_vacations || []).filter(
        (v) => Number(v.id) !== vid,
      );
      return { ...prev, all_vacations: nextAll };
    });
  };

  const handleDelete = (vac) => {
    const vacationId = resolveVacationId(vac);
    if (!vacationId) {
      notification.error({
        message: "تعذر الحذف",
        description: "لم يُعثر على معرّف طلب الإجازة.",
        placement: "bottomLeft",
      });
      return;
    }

    Modal.confirm({
      title: "تأكيد الحذف",
      content:
        "سيتم حذف طلب الإجازة بالكامل (جميع أيام الفترة). لا يمكن التراجع عن هذه العملية.",
      okText: "نعم، احذف",
      okType: "danger",
      cancelText: "إلغاء",
      onOk() {
        return deleteVacationById(vacationId)
          .then(() => {
            removeVacationFromLocalState(vacationId);
            notification.success({
              message: "تم حذف الطلب بنجاح",
              placement: "bottomLeft",
            });
            setIsVacDetailModalVisible(false);
            setRefreshKey((prev) => prev + 1);
          })
          .catch((error) => {
            console.error("خطأ أثناء الحذف:", error);
            notification.error({
              message: "فشل حذف الطلب!",
              description:
                error.response?.data?.message || "حدث خطأ غير متوقع",
              placement: "bottomLeft",
            });
          });
      },
    });
  };
  const handleOk = () => {
    setIsModalVisible(false);
  };
  const openNotification = (placement, text) => {
    notification.success({
      message: text,
      placement,
      duration: 10,
    });
  };
  const hasAttendanceOverlapInRange = (startDateTime, endDateTime) => {
    return hasVacationOverlapWithAttendance(
      startDateTime,
      endDateTime,
      data || [],
      {
        getSegments: (row) =>
          Array.isArray(row?.extra_attendance) ? row.extra_attendance : [],
        preferSegmentsWhenPresent: true,
        getShiftEndForDate: (dateStr) => {
          const shift = dailyShiftRanges?.[dateStr];
          const ends = [];
          if (shift?.end) ends.push(shift.end);
          if (shift?.allowed_end) ends.push(shift.allowed_end);
          if (ends.length) return ends;
          const durationEnd =
            props.setting?.find((i) => i.key === "duration_end")?.value ||
            "14:00:00";
          return `${dateStr} ${String(durationEnd).trim()}`;
        },
      },
    );
  };
  const handleVOk = () => {
    if (!editingRecord?.vac_id && vacationSubmitDisabled) {
      notification.warning({
        message: "تقديم الإجازات موقوف",
        description: "تم إيقاف تقديم الإجازات من الإعدادات العامة.",
        placement: "bottomLeft",
      });
      setSaving(false);
      return;
    }
    if (hasAttendanceOverlapInRange(datefromValue, datetoValue)) {
      notification.error({
        message: "لا يمكن تقديم إجازة داخل وقت بصمة فعلية",
        description: "يمكن تقديم الإجازة فقط في الفترات خارج وقت البصمة.",
        placement: "bottomLeft",
        duration: 8,
      });
      setSaving(false);
      return;
    }

    const noteText = String(form.getFieldValue("notes") ?? "").trim();
    const taskType = form.getFieldValue("task_type") ?? type;

    var values = {
      user_id: props.user.user_id,
      startDate: datefromValue,
      endDate: datetoValue,
      type: taskType,
      note: noteText,
    };

    const endpoint = editingRecord?.vac_id ? `update-task` : `add-task`;
    const payload = editingRecord?.vac_id
      ? { ...values, id: editingRecord.vac_id }
      : values;

    axios
      .post(Env.HOST_SERVER_NAME + endpoint, payload)
      .then(function (response) {
        openNotification(
          "bottomLeft",
          <Text>
            {editingRecord?.vac_id
              ? "تم تحديث الإجازة بنجاح"
              : "تم إرسال الإجازة بنجاح"}
          </Text>,
        );
        setSaving(false);
        setIsVModalVisible(false);
        setNotes("");
        setTotalVac("");
        setDatetoValue("");
        setDatefromValue("");
        setType(null);
        form.resetFields();
        setGivenTasks(null);
        setRestTasks(null);
        // التحديث التلقائي للبيانات بعد الحفظ
        setRefreshKey((prev) => prev + 1);
        props.onUpdate && props.onUpdate();
      })
      .catch(function (error) {
        console.log(error);
        if (error.response.status == 409) {
          notification.error({
            message: error.response.data.message,
            placement: "bottomLeft",
            duration: 10,
          });
          setSaving(false);
        } else {
          notification.error({
            message: "فشل إرسال الإجازة!",
            placement: "bottomLeft",
            duration: 10,
          });
          setSaving(false);
          setIsModalVisible(false);
          setType(null);
          setNotes(null);
        }
      });
  };

  const isEligibleDays = (dateString) => {
    // Calculate the date two days ago
    var daysCount =
      props.setting.filter((item) => item.key == "admin.vacations_tolerance")[0]
        ?.value * 1;
    var count = isNaN(daysCount) ? 1 : daysCount;
    return (
      new Date() >
      new Date(
        new Date(dateString).setDate(new Date(dateString).getDate() + count),
      )
    );
  };

  const columns = [
    {
      title: "اليوم",
      dataIndex: "dayName",
      key: "dayName",
      ellipsis: true,
      render: (dayName, record, _) => <Text>{dayName}</Text>,
    },
    {
      title: "التاريخ",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      sortOrder: sortedInfo.columnKey === "date" && sortedInfo.order,
      ellipsis: true,
      render: (date, record, _) => <Text>{date}</Text>,
    },
    {
      title: "وقت الحضور",
      dataIndex: "attendance_time",
      key: "attendance_time",
      sorter: (a, b) => {
        if (
          a &&
          a.attendance_time &&
          a.attendance_time.length &&
          b &&
          b.attendance_time &&
          b.attendance_time.length
        ) {
          return a.attendance_time.length - b.attendance_time.length;
        } else if (a && a.attendance_time && a.attendance_time.length) {
          return -1;
        } else if (b && b.attendance_time && b.attendance_time.length) {
          return 1;
        }
        return 0;
      },
      sortOrder: sortedInfo.columnKey === "attendance_time" && sortedInfo.order,
      ellipsis: true,
      render: (attendance_time, record, _) => <Text>{attendance_time}</Text>,
    },
    {
      title: "وقت الانصراف",
      dataIndex: "leave_time",
      key: "leave_time",
      sorter: (a, b) => {
        if (
          a &&
          a.leave_time &&
          a.leave_time.length &&
          b &&
          b.leave_time &&
          b.leave_time.length
        ) {
          return a.leave_time.length - b.leave_time.length;
        } else if (a && a.leave_time && a.leave_time.length) {
          return -1;
        } else if (b && b.leave_time && b.leave_time.length) {
          return 1;
        }
        return 0;
      },
      sortOrder: sortedInfo.columnKey === "leave_time" && sortedInfo.order,
      ellipsis: true,
      render: (leave_time, record, _) => <Text>{leave_time}</Text>,
    },
    {
      title: "صافي الدوام",
      dataIndex: "workHours",
      key: "workHours",
      sorter: (a, b) => a.workHours?.localeCompare(b.workHours),
      sortOrder: sortedInfo.columnKey === "workHours" && sortedInfo.order,
      ellipsis: true,
      render: (workHours, record, _) => <Text>{workHours}</Text>,
    },
    {
      title: "التأخرات",
      dataIndex: "lateTime",
      key: "lateTime",
      sorter: (a, b) => a.lateTime.length - b.lateTime.length,
      sortOrder: sortedInfo.columnKey === "lateTime" && sortedInfo.order,
      ellipsis: true,
      render: (_, record) => (
        <Text>{secondsToLateHms(getDisplayedDayLateSeconds(record))}</Text>
      ),
    },
    {
      title: (
        <Tooltip
          title={
            "ليس شرطًا أن تكون البصمة عند نهاية الدوام بالثانية: إن انصرفت قبل نهاية الدوام الرسمية يُحسب نقصًا؛ إن انصرفت عند النهاية أو بعدها فلا خصم من جهة الانصراف. أي بصمات إضافية خارج فترة الدوام المطلوبة تُحسب وقتًا فائضًا فقط ولا تزيد خصم التأخر أو الفجوة. الوقت الفائض يُستخدم لتقديم طلب إضافي من صفحة «الدوام الإضافي»."
          }
        >
          <span>الوقت الفائض (إضافي)</span>
        </Tooltip>
      ),
      dataIndex: "bonusTime",
      key: "bonusTime",
      sorter: (a, b) => (a.bonusTime || "").localeCompare(b.bonusTime || ""),
      sortOrder: sortedInfo.columnKey === "bonusTime" && sortedInfo.order,
      ellipsis: true,
      render: (bonusTime) => (
        <Text>{bonusTime && bonusTime !== "00:00:00" ? bonusTime : "—"}</Text>
      ),
    },
    {
      title: "خصميات",
      dataIndex: "discount",
      key: "discount",
      sorter: (a, b) => a.discount - b.discount,
      sortOrder: sortedInfo.columnKey === "discount" && sortedInfo.order,
      ellipsis: false,
      render: (_, record) => {
        return (
          <Text>
            {Math.round(getDisplayedDayDiscount(record)) + " " + curr}
          </Text>
        );
      },
    },
    {
      title: "النوع",
      dataIndex: "types",
      key: "day_types",
      ellipsis: true,
      render: (_, record) => (
        <Text>
          {pickVacationTypeLabel(record, vacTypeLabelById) ||
            record.types ||
            record.vac_type_name ||
            ""}
        </Text>
      ),
      sorter: (a, b) =>
        (
          pickVacationTypeLabel(a, vacTypeLabelById) ||
          a.types ||
          a.vac_type_name ||
          ""
        ).localeCompare(
          pickVacationTypeLabel(b, vacTypeLabelById) ||
            b.types ||
            b.vac_type_name ||
            "",
        ),
      sortOrder: sortedInfo.columnKey === "day_types" && sortedInfo.order,
    },
    {
      title: "الإجازات",
      width: "80px",
      dataIndex: "have_vac",
      key: "have_vac",
      ellipsis: true,
      render: (have_vac, record, _) => {
        let statusText = "في انتظار الاعتماد";
        let color = "#8c8c8c"; // الافتراضي رمادي (في انتظار الاعتماد)
        if (record.vac_status == 4) {
          color = "#991b1b"; // أحمر داكن (مرفوضة من الشؤون)
          statusText = "مرفوضة من الشؤون";
        } else if (record.vac_status == 3) {
          color = "#f87171"; // أحمر فاتح (مرفوضة من مدير الإدارة)
          statusText = "مرفوضة من مدير الإدارة";
        } else if (record.vac_status == 2) {
          color = "#0972B6"; // أزرق (معتمدة من الشؤون)
          statusText = "معتمدة من الشؤون";
        } else if (record.vac_status == 1) {
          color = "#52c41a"; // أخضر (معتمدة من مدير الإدارة)
          statusText = "معتمدة من مدير الإدارة";
        }

        return (
          <>
            {(record.have_vac == "1" || record.vac_status != null) && (
              <div
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setCurrentVacDetail({
                    ...record,
                    statusText: statusText,
                    color: color,
                  });
                  setIsVacDetailModalVisible(true);
                }}
              >
                <CopyOutlined
                  size={100}
                  style={{
                    marginLeft: "10px",
                    color: color,
                    fontSize: record.vac_status > 0 ? "18px" : "16px",
                  }}
                />
              </div>
            )}
          </>
        );
      },
    },
    {
      title: "التفاصيل",
      key: "action",
      render: (vid, record, index) => {
        return (
          <Button
            onClick={function () {
              showModal(record);
            }}
            type="primary"
            shape="round"
            icon={<SwapOutlined />}
          ></Button>
        );
      },
    },
    {
      title: "تقديم",
      key: "action",
      render: (vid, record, index) => {
        return (
          <Button
            disabled={isEligibleDays(record.date) || vacationSubmitDisabled}
            title={
              vacationSubmitDisabled
                ? "تم إيقاف تقديم الإجازات من الإعدادات"
                : undefined
            }
            onClick={function () {
              // النقر هنا يعني دائماً إضافة إجازة جديدة لهذا اليوم
              showVacationModal(record, false);
            }}
            type="primary"
            shape="round"
            style={{ backgroundColor: "#FAA61A", border: "none" }}
            icon={<FormOutlined />}
          ></Button>
        );
      },
    },
  ];
  const dcolumns = [
    {
      title: "زمن الدخول",
      dataIndex: "attendance_time",
      key: "attendance_time",
      sorter: (a, b) => a.attendance_time.length - b.attendance_time.length,
      sortOrder: sortedInfo.columnKey === "attendance_time" && sortedInfo.order,
      ellipsis: true,
      render: (attendance_time) =>
        (attendance_time?.includes(" ")
          ? attendance_time.split(" ")[1]
          : attendance_time) || "-",
    },
    {
      title: "زمن الخروج",
      dataIndex: "leave_time",
      key: "leave_time",
      sorter: (a, b) => a.leave_time.length - b.leave_time.length,
      sortOrder: sortedInfo.columnKey === "leave_time" && sortedInfo.order,
      ellipsis: true,
      render: (leave_time) =>
        (leave_time?.includes(" ") ? leave_time.split(" ")[1] : leave_time) ||
        "-",
    },
    {
      title: "ساعات العمل",
      dataIndex: "workHour",
      key: "workHour",
      sorter: (a, b) => a.workHour.length - b.workHour.length,
      sortOrder: sortedInfo.columnKey === "workHour" && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: "التأخرات",
      dataIndex: "lateTime",
      key: "lateTime",
      sorter: (a, b) => a.lateTime.length - b.lateTime.length,
      sortOrder: sortedInfo.columnKey === "lateTime" && sortedInfo.order,
      ellipsis: true,
      render: (lateTime) => lateTime?.toHHMMSS(),
    },
    {
      title: "الدوام الإضافي",
      dataIndex: "bonusTime",
      key: "bonusTime",
      sorter: (a, b) => a.bonusTime.length - b.bonusTime.length,
      sortOrder: sortedInfo.columnKey === "bonusTime" && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: "نوع الدوام",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order,
      ellipsis: true,
    },
  ];
  String.prototype.toHHMMSS = function () {
    var sec_num = parseInt(this, 10); // don't forget the second param
    var hours = Math.floor(sec_num / 3600);
    var minutes = Math.floor((sec_num - hours * 3600) / 60);
    var seconds = sec_num - hours * 3600 - minutes * 60;

    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return hours + ":" + minutes + ":" + seconds;
  };
  const convertTimeToSeconds = (fullTime) => {
    var seconds = 0;
    if (fullTime == null || fullTime == 0) {
      seconds = 0;
    } else {
      var time = fullTime.split(":");
      seconds =
        parseInt(time[0]) * 60 * 60 +
        parseInt(time[1]) * 60 +
        parseInt(time[2]);
    }
    return seconds;
  };
  const formatRestTime = (restValue) => {
    if (restValue == null || restValue === "") return "0:0";
    if (typeof restValue === "string" && restValue.includes(":")) {
      const parts = restValue.split(":");
      const hh = Number(parts[0]) || 0;
      const mm = Number(parts[1]) || 0;
      return `${hh}:${mm}`;
    }
    const totalMinutes = Number(restValue) || 0;
    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    return `${hh}:${mm}`;
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };
  const handleVCancel = () => {
    setIsVModalVisible(false);
    setDatefromValue(null);
    setDatetoValue(null);
    setTotalVac("");
    setType(null);
    setNotes("");
    form.resetFields();
  };
  const handleTypeChange = (e) => {
    setType(e);
    form.setFieldsValue({ task_type: e });
    getGivenRest(e);
  };
  const getGivenRest = (e) => {
    const requestId = ++givenRequestRef.current;
    setGivenLoad(true);
    axios
      .get(
        Env.HOST_SERVER_NAME +
          "given-tasks/" +
          props.user.user_id +
          "/" +
          start +
          "/" +
          end,
      )
      .then((response) => {
        if (requestId !== givenRequestRef.current) return;
        const fromGiven = response.data.tasksAmount || [];
        let tasksAmountForDisplay = fromGiven;
        if (
          !findVacationBalanceByType(fromGiven, e) &&
          Array.isArray(vacationsAmount) &&
          vacationsAmount.length
        ) {
          tasksAmountForDisplay = [...fromGiven, ...vacationsAmount];
        }
        const { given, rest } = buildGivenRestDisplay(
          tasksAmountForDisplay,
          e,
        );
        const selectedType = (tstypes || []).find(
          (item) => Number(item?.value) === Number(e),
        );
        const isExemptionType = isExemptionVacationLabel(selectedType?.label);

        if (isExemptionType) {
          setGivenTasks(given);
          setRestTasks(buildExemptionRestFromGivenDisplay(given, "00:00"));
        } else {
          setGivenTasks(given);
          setRestTasks(rest);
        }

        setGivenLoad(false);
      })
      .catch(function (error) {
        if (requestId !== givenRequestRef.current) return;
        console.log(error);
        setGivenLoad(false);
      });
  };
  const onChange = (monthValue) => {
    if (!monthValue) return;
    setCurrentMonth(monthValue.format("MMMM"));
    const range = getPayrollRangeForMonth(monthValue, props.setting);
    setStart(range.start);
    const monthEnd = dayjs(range.end, "YYYY-MM-DD");
    setEnd(
      dayjs().isBefore(monthEnd)
        ? dayjs().format("YYYY-MM-DD")
        : range.end,
    );
    setRefreshKey((prev) => prev + 1);
  };
  const checkPeriod = (all, date) => {
    if (date[1] !== "" && date[0]) {
      fetchVacationDurationLabel(
        props.user.user_id,
        date[0],
        date[1],
        setTotalVac,
      );
    }
  };
  const resolveVacationShiftRange = useCallback(
    async (dateStr, rowHint) => {
      if (!dateStr) return null;

      if (rowHint?.shift_start && rowHint?.shift_end) {
        const fromRow = shiftWindowToFormRange({
          start: rowHint.shift_start,
          end: rowHint.shift_end,
        });
        if (fromRow) return fromRow;
      }

      const fromServer = await fetchEmployeeShiftWindow(
        props.user.user_id,
        dateStr,
      );
      if (fromServer) return fromServer;
      return getLocalShiftRangeForDate(
        dateStr,
        dailyShiftRanges,
        durationFallback,
      );
    },
    [props.user.user_id, dailyShiftRanges, durationFallback],
  );

  const applyVacationShiftToForm = useCallback(
    async (startDateStr, endDateStr, rowHint) => {
      const endDay = endDateStr || startDateStr;
      if (!startDateStr) return;

      let fromValue;
      let toValue;

      if (startDateStr === endDay) {
        const range = await resolveVacationShiftRange(startDateStr, rowHint);
        if (!range) return;
        fromValue = range.fromValue;
        toValue = range.toValue;
      } else {
        const startRange = await resolveVacationShiftRange(startDateStr, rowHint);
        const endRange = await resolveVacationShiftRange(endDay, rowHint);
        if (!startRange || !endRange) return;
        fromValue = startRange.fromValue;
        toValue = endRange.toValue;
      }

      setDatefromValue(fromValue);
      setDatetoValue(toValue);
      const fromDate = parseVacationDateValue(fromValue);
      const toDate = parseVacationDateValue(toValue);
      form.setFieldsValue({
        date_range: fromDate && toDate ? [fromDate, toDate] : undefined,
      });
      if (fromDate && toDate) {
        checkPeriod(null, [fromDate, toDate]);
      }
    },
    [resolveVacationShiftRange, form],
  );

  const onRangeChange = (all, dateStrings) => {
    if (dateStrings?.[0] && dateStrings?.[1]) {
      checkPeriod(all, dateStrings);
    }
    let fromStr = null;
    let toStr = null;
    if (all?.[0]) fromStr = all[0].format("YYYY-MM-DD HH:mm");
    if (all?.[1]) toStr = all[1].format("YYYY-MM-DD HH:mm");
    setDatefromValue(fromStr);
    setDatetoValue(toStr);
    if (fromStr && toStr) {
      form.setFieldsValue({
        date_range: [all[0], all[1]],
      });
    }
  };

  const onRangeOk = (all) => {
    if (all?.[0] && all?.[1]) {
      const fromStr = all[0].format("YYYY-MM-DD HH:mm");
      const toStr = all[1].format("YYYY-MM-DD HH:mm");
      setDatefromValue(fromStr);
      setDatetoValue(toStr);
      form.setFieldsValue({
        date_range: [all[0], all[1]],
      });
      checkPeriod(all, [fromStr, toStr]);
    }
  };

  useEffect(() => {
    if (!isVModalVisible || !editingRecord) {
      if (!isVModalVisible) {
        setEditingRecord(null);
        setNotes("");
      }
      return;
    }

    if (editingRecord.vac_id) {
      const fromValue = editingRecord.vac_date_from;
      const toValue = editingRecord.vac_date_to;
      const typeId = Number(
        editingRecord.vacationtype_id || editingRecord.type,
      );
      const fromDate = parseVacationDateValue(fromValue);
      const toDate = parseVacationDateValue(toValue);

      setDatefromValue(fromValue);
      setDatetoValue(toValue);
      setType(typeId);
      setNotes(editingRecord.vac_note || "");

      const timeoutId = setTimeout(() => {
        form.setFieldsValue({
          date_range: [fromDate, toDate],
          task_type: typeId,
          notes: editingRecord.vac_note || "",
        });
        if (fromDate && toDate) checkPeriod(null, [fromDate, toDate]);
        getGivenRest(typeId);
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    const recordDate =
      editingRecord.date ||
      editingRecord.vac_date ||
      dayjs().format("YYYY-MM-DD");

    setNotes("");
    setType(null);
    form.setFieldsValue({
      date_range: undefined,
      task_type: undefined,
      notes: "",
    });

    applyVacationShiftToForm(recordDate, recordDate, editingRecord);
  }, [isVModalVisible, editingRecord, applyVacationShiftToForm]);

  return (
    <Layout className="attendance">
      <VacationSubmitModal
        modalKey={
          isVModalVisible
            ? editingRecord?.vac_id || editingRecord?.date || "new"
            : "none"
        }
        open={isVModalVisible}
        title="تقديم إجازة / مهمة"
        confirmLoading={saving}
        onOk={function () {
          setSaving(true);
          handleVOk();
        }}
        onCancel={handleVCancel}
        form={form}
        totalVac={totalVac}
        givenTasks={givenTasks}
        restTasks={restTasks}
        rangePickerProps={{
          needConfirm: true,
          inputReadOnly: window.innerWidth <= 760,
          disabledDate: disabledDate,
          value: [
            parseVacationDateValue(datefromValue),
            parseVacationDateValue(datetoValue),
          ],
          showTime: {
            format: "HH:mm",
          },
          format: "YYYY-MM-DD HH:mm",
          onChange: onRangeChange,
          onOk: onRangeOk,
        }}
        taskTypeSelectProps={{
          onChange: handleTypeChange,
          options: tstypes,
        }}
      />
      <Modal
        centered
        title="تفاصيل الإجازات / المهام لهذا اليوم"
        visible={isVacDetailModalVisible}
        onCancel={() => setIsVacDetailModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setIsVacDetailModalVisible(false)}
          >
            إغلاق
          </Button>,
        ]}
        width={600}
      >
        {currentVacDetail && (
          <div
            style={{ maxHeight: "60vh", overflowY: "auto", padding: "10px" }}
          >
            {(() => {
              // محاولة الحصول على القائمة من السجل، أو إعادة حسابها من القائمة الرئيسية كحل احتياطي (Self-healing)
              let displayVacations = currentVacDetail.all_vacations || [];

              if (displayVacations.length === 0 && vacations.length > 0) {
                const currentDay = dayjs(currentVacDetail.date);
                displayVacations = vacations.filter((v) => {
                  if (!v.date_from || !v.date_to) return false;
                  const vacStart = dayjs(v.date_from).startOf("day");
                  const vacEnd = dayjs(v.date_to).endOf("day");
                  return (
                    currentDay.isSame(vacStart, "day") ||
                    currentDay.isSame(vacEnd, "day") ||
                    (currentDay.isAfter(vacStart) &&
                      currentDay.isBefore(vacEnd))
                  );
                });
              }

              if (displayVacations.length === 0) {
                // إذا لم نجد شيئاً في القائمتين، ولكن السجل يشير لوجود إجازة (Fallback Virtual Entry)
                if (
                  currentVacDetail.vac_status != null ||
                  currentVacDetail.have_vac == "1"
                ) {
                  displayVacations = [
                    {
                      id: currentVacDetail.vac_id || "virtual",
                      accepted:
                        currentVacDetail.accepted ||
                        currentVacDetail.vac_status ||
                        0,
                      vac_type_name:
                        currentVacDetail.vac_type_name ||
                        currentVacDetail.types ||
                        "",
                      vacationtype_id: currentVacDetail.vacationtype_id,
                      vac_id: currentVacDetail.vac_id,
                      vid: currentVacDetail.vid,
                      types: currentVacDetail.types,
                      date_from:
                        currentVacDetail.vac_date_from ||
                        currentVacDetail.date + " 00:00:00",
                      date_to:
                        currentVacDetail.vac_date_to ||
                        currentVacDetail.date + " 23:59:59",
                      description:
                        currentVacDetail.notes || currentVacDetail.vac_note,
                      is_virtual: true,
                    },
                  ];
                }
              }

              if (displayVacations.length === 0) {
                return (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Text type="secondary">
                      لا توجد تفاصيل إجازات لهذا اليوم.
                    </Text>
                  </div>
                );
              }

              return displayVacations.map((vac, idx) => {
                let statusText = "في انتظار الاعتماد";
                let color = "#8c8c8c";
                const vStatus = Number(vac.accepted);

                // التنسيق حسب الحالة المعرفة في النظام
                if (vStatus === 2) {
                  color = "#0972B6";
                  statusText = "معتمدة من الشؤون";
                } else if (vStatus === 1) {
                  color = "#52c41a";
                  statusText = "معتمدة من مدير الإدارة";
                } else if (vStatus === 3) {
                  color = "#f87171";
                  statusText = "مرفوضة من مدير الإدارة";
                } else if (vStatus === 4) {
                  color = "#991b1b";
                  statusText = "مرفوضة من الشؤون";
                }

                return (
                  <Card
                    key={vac.id || idx}
                    style={{
                      marginBottom: "16px",
                      borderRight: `5px solid ${color}`,
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                    size="small"
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "8px",
                      }}
                    >
                      <Text
                        strong
                        style={{ fontSize: "16px", color: "#262626" }}
                      >
                        {pickVacationTypeLabel(vac, vacTypeLabelById) || "—"}
                      </Text>
                      <Tag
                        color={
                          color === "#0972B6"
                            ? "blue"
                            : color === "#52c41a"
                              ? "green"
                              : "default"
                        }
                        style={{ margin: 0, fontWeight: "bold" }}
                      >
                        {statusText}
                      </Tag>
                    </div>

                    <div
                      style={{
                        marginBottom: "8px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <Text type="secondary">الوقت: </Text>
                        <Text strong>
                          {(() => {
                            const slice = computeVacationDayShiftSlice(
                              currentVacDetail.date,
                              vac,
                              dailyShiftRanges,
                              durationFallback,
                            );
                            return `${slice.from} - ${slice.to}`;
                          })()}
                        </Text>
                      </div>
                      <div
                        style={{
                          borderLeft: "1px solid #f0f0f0",
                          paddingLeft: "10px",
                        }}
                      >
                        <Text type="secondary">المدة: </Text>
                        <Text strong style={{ color: "#389e0d" }}>
                          {(() => {
                            const slice = computeVacationDayShiftSlice(
                              currentVacDetail.date,
                              vac,
                              dailyShiftRanges,
                              durationFallback,
                            );
                            const minutes = Math.floor((slice.secs || 0) / 60);
                            const h = Math.floor(minutes / 60);
                            const m = minutes % 60;
                            return (
                              (h > 0 ? `${h} س ` : "") + (m > 0 ? `${m} د` : "")
                            );
                          })()}
                        </Text>
                      </div>
                    </div>

                    {(vac.description || vac.notes) && (
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "8px",
                          background: "#fafafa",
                          borderRadius: "4px",
                          fontSize: "13px",
                        }}
                      >
                        <Text type="secondary">ملاحظات: </Text>
                        <Text>{vac.description || vac.notes}</Text>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginTop: "12px",
                        borderTop: "1px solid #f0f0f0",
                        paddingTop: "8px",
                      }}
                    >
                      {canMutateAttendanceVacation(vac, props.setting) ? (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon={<FormOutlined />}
                            onClick={() => {
                              showVacationModal(
                                {
                                  ...vac,
                                  date: currentVacDetail.date,
                                  // تعبئة الحقول المطلوبة لنموذج التعديل ليتناسب مع useEffect
                                  vac_id: vac.id,
                                  vac_date_from: vac.date_from,
                                  vac_date_to: vac.date_to,
                                  type: vac.vacationtype_id,
                                  vac_note: vac.description || vac.notes,
                                },
                                true,
                              );
                              setIsVacDetailModalVisible(false);
                            }}
                          >
                            تعديل
                          </Button>
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={
                              <Text type="danger" style={{ color: "inherit" }}>
                                X
                              </Text>
                            }
                            onClick={() => handleDelete(vac)}
                          >
                            حذف
                          </Button>
                        </>
                      ) : (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          لا يمكن التعديل أو الحذف بعد اعتماد الشؤون
                        </Text>
                      )}
                    </div>
                  </Card>
                );
              });
            })()}
          </div>
        )}
      </Modal>
      <Modal
        centered
        className="att-model"
        width={1100}
        title={"أحداث اليوم | " + detailedDay}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <AppTable
          style={{ textAlign: "center!important" }}
          scroll={{ x: "1000px" }}
          columns={dcolumns}
          dataSource={selected}
          onChange={handleChange}
        />
      </Modal>
      <Card bordered>
        <div className="attHeader">
          <div className="attPer">
            <span>
              <Progress
                type="circle"
                percent={Math.round(localTotals.disciplinePercentage * 100)}
                width={window.innerWidth <= 760 ? 60 : 70}
                style={{ marginLeft: "5px", display: "inline-block" }}
              />
            </span>
            {window.innerWidth <= 760 ? (
              <>{localTotals.attDays + " من " + totalDays} يوما </>
            ) : (
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: "10px",
                  marginRight: "5px",
                }}
              >
                <div style={{ marginBottom: "5px" }}>
                  الدوام المطلوب : <span>{totalDays}</span> يوم{" "}
                </div>
                <div>
                  الدوام الفعلي : <span>{localTotals.attDays}</span> يوم{" "}
                </div>
              </span>
            )}
          </div>
          <div className="disPer">
            {/* <span>
              <Progress
                type="circle"
                percent={Math.round(localTotals.disciplinePercentage * 100)}
                width={window.innerWidth <= 760 ? 60 : 70}
                style={{ marginLeft: "5px", display: "inline-block" }}
              />
            </span> */}
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                paddingTop: "10px",
                marginRight: "5px",
                fontSize: "14px",
              }}
            >
              <div style={{ marginBottom: "5px" }}>
                التأخرات :{" "}
                {window.innerWidth <= 760 ? (
                  <>
                    {parseInt(localTotals.lateMinutes / 60) +
                      ":" +
                      (localTotals.lateMinutes % 60)}
                  </>
                ) : (
                  <span>
                    {parseInt(localTotals.lateMinutes / 60)} ساعة و{" "}
                    {localTotals.lateMinutes % 60} دقيقة{" "}
                  </span>
                )}
              </div>
              <div>
                {window.innerWidth <= 760 ? "الخصم" : "إجمالي الخصم"} :{" "}
                <span>{roundDiscount(localTotals.discounts)}</span> {curr}
              </div>
            </span>
          </div>
          <div className="attOper" style={{ marginBottom: "20px" }}>
            {window.innerWidth <= 760 ? (
              <></>
            ) : (
              <div style={{ marginLeft: "10px" }}>
                <span>اختر شهرًا : </span>
                <DatePicker
                  needConfirm={false}
                  inputReadOnly={window.innerWidth <= 760}
                  defaultValue={dayjs()}
                  onChange={onChange}
                  picker="month"
                />
              </div>
            )}
            <div className="attOperRange">
              <span>اختر فترة : </span>
              <RangePicker
                needConfirm={true}
                inputReadOnly={window.innerWidth <= 760}
                value={[dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")]}
                style={{ width: "230px" }}
                onChange={changeRange}
              />
            </div>
            <div className="attOperBtn" style={{ textAlign: "left" }}>
              {window.innerWidth <= 760 ? (
                <></>
              ) : (
                <Button
                  disabled={load}
                  style={{
                    margin: "0 10px",
                    textAlign: "center",
                    marginLeft: "5px",
                  }}
                  onClick={function () {
                    exportToExcel("xlsx");
                  }}
                  type="primary"
                >
                  <ExportOutlined />
                </Button>
              )}
              <Button
                disabled={load}
                style={{ backgroundColor: "#0972B6", borderColor: "#0972B6" }}
                onClick={function () {
                  printReport();
                }}
                type="primary"
              >
                <PrinterOutlined />
              </Button>
            </div>
          </div>
        </div>
        <AppTable
          loading={load}
          style={{ textAlign: "center!important" }}
          columns={columns}
          scroll={{ x: "1000px" }}
          onRow={(record, rowIndex) => {
            var bc;
            // إذا كان اليوم مستثنى، لا نطبق اللون البرتقالي
            const isExcluded =
              record.is_excluded === 1 ||
              record.is_excluded === "1" ||
              weekendDays.includes(record.dayName) ||
              excludedDates.includes(record.date);

            // اللون البرتقالي يظهر فقط للأيام غير المستثناة التي لا يوجد فيها حضور ولم يتم اعتماد إجازة لها
            if (
              !isExcluded &&
              !recordHasCompleteAttendance(record) &&
              !(record.vac_status >= 1)
            ) {
              bc = "#FCEF96";
            }

            return {
              className: record.status,
              style: { backgroundColor: bc },
            };
          }}
          dataSource={data}
          onChange={handleChange}
        />
      </Card>
      <div id="att-report" style={{ display: "none" }}>
        <div
          style={{
            direction: "rtl",
            fontSize: "12px",
            fontFamily:
              "Tajawal, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            margin: "0",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "row",
              borderColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
            }}
          >
            <div style={{ width: "30%" }}>
              <img
                loading="eager"
                style={{ width: "320px" }}
                src={
                  Env.HOST_SERVER_STORAGE +
                  props.setting.filter((item) => item.key == "admin.logo")[0]
                    ?.value
                }
              />
            </div>
            <div
              style={{
                fontSize: "11px",
                textAlign: "center",
                width: "35%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                paddingBottom: "10px",
              }}
            >
              <h1
                style={{ fontSize: " 18px", marginBottom: " 5px", margin: "0" }}
              >
                السجل التفصيلي للموظف
              </h1>
              <h2
                style={{ fontSize: " 14px", fontWeight: " 200", margin: "0" }}
              >
                للفترة من {start} إلى {end}
              </h2>
            </div>
            <div style={{ width: "35%" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  margin: "20px 5px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingTop: "10px",
                      marginRight: "5px",
                    }}
                  >
                    <div style={{ marginBottom: "5px" }}>
                      الدوام المطلوب : <span>{totalDays}</span> يوم{" "}
                    </div>
                    <div>
                      الدوام الفعلي : <span>{localTotals.attDays}</span>{" "}
                      يوم{" "}
                    </div>
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingTop: "10px",
                      marginRight: "5px",
                    }}
                  >
                    <div style={{ marginBottom: "5px" }}>
                      التأخرات : <span>{localTotals.lateMinutes}</span>{" "}
                      دقيقة{" "}
                    </div>
                    <div>
                      إجمالي الخصم :{" "}
                      <span>{roundDiscount(localTotals.discounts)}</span> {curr}
                    </div>
                    <div>
                      نسبة الانضباط:{" "}
                      {Math.round(localTotals.disciplinePercentage * 100)}%
                    </div>
                  </span>
                </div>
              </div>
            </div>
          </header>
          <div
            class="table-info"
            style={{
              display: "flex",
              flexDirection: "row",
              textAlign: "center",
              padding: "10px",
              fontSize: "14px",
              borderBottom: "1px solid black",
            }}
          >
            <div style={{ width: " 30%" }}>الاسم: {props.user.name}</div>
            <div style={{ width: " 20%" }}>
              {" "}
              الرقم الوظيفي: {props.user.user_id}{" "}
            </div>
            <div style={{ width: " 20%" }}>الوظيفة: {props.user.job}</div>
            <div style={{ width: " 30%" }}>
              الإدارة:{" "}
              {typeof props.user.category === "object"
                ? props.user.category.name
                : props.user.category}
            </div>
          </div>
          <div>
            <table
              style={{
                fontSize: "12px",
                width: " 100%",
                textAlign: " center",
                marginTop: " 20px",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>اليوم</th>
                  <th style={{ fontWeight: "100" }}>التاريح</th>
                  <th style={{ fontWeight: "100" }}>الحضور</th>
                  <th style={{ fontWeight: "100" }}>الانصراف</th>
                  <th style={{ fontWeight: "100" }}>ساعات العمل</th>
                  <th style={{ fontWeight: "100" }}>التأخرات</th>
                  <th style={{ fontWeight: "100" }}>الإجازات</th>
                  <th style={{ fontWeight: "100" }}>نوع الإجازة</th>
                  <th style={{ fontWeight: "100" }}>الوقت الفائض</th>
                  <th style={{ fontWeight: "100" }}>مبلغ الخصم</th>
                  <th style={{ fontWeight: "100", width: " 300px" }}>
                    ملاحظات
                  </th>
                </tr>
              </thead>
              <tbody>
                {pdata.flatMap((item, idx) => {
                  // التحقق من إذا كان اليوم مستثنى لغرض التلوين
                  const isExcluded =
                    item.is_excluded === 1 ||
                    item.is_excluded === "1" ||
                    weekendDays.includes(item.dayName) ||
                    excludedDates.includes(item.date);

                  // تحقق من وجود بصمات متعددة في نفس اليوم
                  // نعتبرها إضافية فقط إذا كان هناك أكثر من زوج (أكثر من عنصر واحد في المصفوفة)
                  const extraAttendance = Array.isArray(item.extra_attendance)
                    ? item.extra_attendance
                    : [];
                  const hasExtra = extraAttendance.length > 1;

                  // تحديد اللون الأساسي للصف
                  const bgColor = isExcluded
                    ? idx % 2 !== 0
                      ? "#e6e6e6"
                      : "#fff"
                    : item.attendance_time || item.discount == 0 || item.types
                      ? idx % 2 !== 0
                        ? "#e6e6e6"
                        : "#fff"
                      : "rgb(233 184 184)";

                  // الصف الأساسي (يحتوي على البيانات الإجمالية وأول زوج بصمات)
                  const firstIn = hasExtra
                    ? formatTime(extraAttendance[0].in)
                    : item.attendance_time;
                  const firstOut = hasExtra
                    ? formatTime(extraAttendance[0].out)
                    : item.leave_time;
                  const dayVacations = getDayVacationsForRow(
                    item,
                    vacations,
                    vacTypeLabelById,
                    dailyShiftRanges,
                    durationFallback,
                  );

                  const baseRow = (
                    <tr
                      key={`${idx}-0`}
                      style={{
                        height: "25px",
                        backgroundColor: bgColor,
                      }}
                    >
                      <td>{item.dayName}</td>
                      <td>{item.date}</td>
                      <td>{firstIn}</td>
                      <td>{firstOut}</td>
                      <td>{item.workHours}</td>
                      <td>
                        {secondsToLateHms(getDisplayedDayLateSeconds(item))}
                      </td>
                      <td>{dayVacations.length ? "" : item.vacHours || ""}</td>
                      <td>
                        {dayVacations.length
                          ? ""
                          : item.types
                            ? item.types
                            : ""}
                      </td>
                      <td>{item.bonusTime}</td>
                      <td>
                        {new Intl.NumberFormat("en-EN").format(
                          roundDiscount(getDisplayedDayDiscount(item)),
                        ) +
                          " " +
                          curr}
                      </td>
                      <td style={{ width: "300px" }}>{item.notes}</td>
                    </tr>
                  );

                  // البصمات الإضافية (نكرر فقط الوقت، وباقي الخلايا علامة "-")
                  const extraRows = hasExtra
                    ? extraAttendance.slice(1).map((extra, i) => (
                        <tr
                          key={`${idx}-extra-${i}`}
                          style={{
                            height: "25px",
                            backgroundColor: bgColor, // استخدام نفس لون اليوم الأساسي
                            borderTop: "1px dashed #ccc", // فاصل بسيط بين البصمات
                          }}
                        >
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{formatTime(extra.in)}</td>
                          <td>{formatTime(extra.out)}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                          <td>{"-"}</td>
                        </tr>
                      ))
                    : [];

                  const vacationRows = dayVacations.map((vac, i) => (
                    <tr
                      key={`${idx}-vac-${vac.id || i}`}
                      style={{
                        height: "25px",
                        backgroundColor: bgColor,
                        borderTop: "1px dashed #ccc",
                      }}
                    >
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{vac.from || "-"}</td>
                      <td>{vac.to || "-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{formatSecondsToHHMM(vac.secs)}</td>
                      <td>{vac.typeLabel || "-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                    </tr>
                  ));

                  return [baseRow, ...extraRows, ...vacationRows];
                })}

                {/* إجمالي الصفوف */}
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <td colSpan={4}>الإجمالي</td>
                  <td>{localTotals.workSecs.toString().toHHMMSS()}</td>
                  <td>{localTotals.lateSeconds.toString().toHHMMSS()}</td>
                  <td>{localTotals.vacSecs.toString().toHHMMSS()}</td>
                  <td>{"-"}</td>
                  <td>{localTotals.bonusSecs.toString().toHHMMSS()}</td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      roundDiscount(localTotals.discounts),
                    ) +
                      " " +
                      curr}
                  </td>
                  <td>{"-"}</td>
                </tr>
              </tbody>

              {/* <tbody>
             
             {pdata.map(item=>{

               allWorkHours+=convertTimeToSeconds(item.workHours);
               allLateTimes+=convertTimeToSeconds(item.lateTime);
               allVacHours+=convertTimeToSeconds(item.vacHours);
               allBonusTimes+=convertTimeToSeconds(item.bonusTime);
               allDiscounts+=item.discount*1;

               return(
              <tr style={{height: " 25px",backgroundColor:item.attendance_time || item.discount==0 || item.types ?data.indexOf(item) %2!=0?'#e6e6e6':'#fff':'rgb(233 184 184)'}}>
                <td>{item.dayName}</td>
                <td>{item.date}</td>
                <td>{item.attendance_time}</td>
                <td>{item.leave_time}</td>
                <td>{item.workHours}</td>
                <td>{item.lateTime}</td>
                <td>{item.vacHours}</td>
                <td>{item.types?item.types:''}</td>
                <td>{item.bonusTime}</td>
                <td>{new Intl.NumberFormat('en-EN').format(Math.round(item.discount))+" "+curr}</td>
                <td style={{width: "300px"}}>{item.notes}</td>
              </tr>);
             })}
             <tr style={{color:"#fff",backgroundColor: "#0972B6",height: "30px"}}>
                <td colSpan={4}>الإجمالي</td>
                <td>{allWorkHours.toString().toHHMMSS()}</td>
                <td>{allLateTimes.toString().toHHMMSS()}</td>
                <td>{allVacHours.toString().toHHMMSS()}</td>
                <td>{"-"}</td>
                <td>{allBonusTimes.toString().toHHMMSS()}</td>
                <td>{new Intl.NumberFormat('en-EN').format(Math.round(allDiscounts))+" "+curr}</td>
                <td>{"-"}</td>
                </tr>
            </tbody> */}
            </table>
          </div>
          <div
            style={{ display: "flex", flexDirection: "row", marginTop: "20px" }}
          >
            <table
              style={{
                fontSize: "12px",
                width: "50%",
                textAlign: "center",
                paddingLeft: "20px",
              }}
            >
              <caption style={{ fontWeight: "900" }}>خلاصة الإجازات</caption>
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>نوع الإجازة</th>
                  {vacationsTypes.map((item, idx) => (
                    <th key={idx} style={{ fontWeight: "100" }}>
                      {item.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ backgroundColor: " #0972B6", color: "#fff" }}>
                    الممنوحة
                  </td>
                  {vacationsTypes.map((item, idx) => {
                    const { given } = buildGivenRestDisplay(
                      periodTasksAmount,
                      item.id,
                    );
                    return <td key={idx}>{given === "-" ? "" : given}</td>;
                  })}
                </tr>
                <tr style={{ backgroundColor: "#e6e6e6" }}>
                  <td style={{ backgroundColor: "#0972B6", color: "#fff" }}>
                    المتبقية
                  </td>
                  {vacationsTypes.map((item, idx) => {
                    const { given, rest } = buildGivenRestDisplay(
                      periodTasksAmount,
                      item.id,
                    );
                    const displayRest = isExemptionVacationLabel(item?.name)
                      ? buildExemptionRestFromGivenDisplay(given, "00:00")
                      : rest === "-"
                        ? "00:00"
                        : rest;
                    return <td key={idx}>{displayRest}</td>;
                  })}
                </tr>
              </tbody>
            </table>
            <table
              style={{
                fontSize: "12px",
                width: "50%",
                textAlign: "center",
                paddingRight: "20px",
              }}
            >
              <caption style={{ fontWeight: "900" }}>خلاصة الخصميات</caption>
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>نوع الخصم</th>
                  <th style={{ fontWeight: "100" }}>الاستحقاق</th>
                  <th style={{ fontWeight: "100" }}>غياب</th>
                  <th style={{ fontWeight: "100" }}>تأخرات</th>
                  <th style={{ fontWeight: "100" }}>سلفة</th>
                  <th style={{ fontWeight: "100" }}>أقساط</th>
                  <th style={{ fontWeight: "100" }}>الإجمالي</th>
                  <th style={{ fontWeight: "100" }}>صافي الاستحقاق</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ height: "20px" }}>
                  <td style={{ backgroundColor: "#0972B6", color: "#fff" }}>
                    المبلغ
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      props.user.status == 16
                        ? props.user.salary
                        : props.user.salary * pdata.length,
                    ) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      roundDiscount(localTotals.absentDeductions),
                    ) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      roundDiscount(localTotals.lateDeductions),
                    ) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(totalDebt) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(totalLoan) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      Math.round(
                        Number(localTotals.totalDeductions) +
                          Number(totalDebt) +
                          Number(totalLoan),
                      ),
                    ) +
                      " " +
                      curr}
                  </td>
                  <td>
                    {new Intl.NumberFormat("en-EN").format(
                      Math.max(
                        0,
                        (props.user.status == 16
                          ? Number(props.user.salary)
                          : Number(props.user.salary) * pdata.length) -
                          Math.round(
                            Number(localTotals.totalDeductions) +
                              Number(totalDebt) +
                              Number(totalLoan),
                          ),
                      ),
                    ) +
                      " " +
                      curr}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50%", fontWeight: "900" }}>المختص</div>
            <div style={{ width: "50%", fontWeight: "900" }}>مدير الشؤون</div>
          </div>
          <div
            style={{
              marginTop: " 20px",
              width: "85%",
              backgroundColor: "#e6e6e61",
              padding: "5px 0",
              borderTopLeftRadius: " 5px",
              borderBottomLeftRadius: " 5px",
            }}
          >
            <div
              style={{
                backgroundColor: " #0972B6",
                width: " 95%",
                height: " 15px",
                borderTopLeftRadius: " 5px",
                borderBottomLeftRadius: " 5px",
                color: " #fff",
                paddingRight: " 20px",
              }}
            >
              نظام دوام | {new Date().toLocaleString("en-IT")}{" "}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
